import Oidc from 'oidc-client';
import analytics from '@api/analytics';
import eventBus from '@api/eventBus';
import store from '../store';
import { clone } from 'lodash';

const _ = { clone };

let manager = null;

/**
 * Initialises the OIDC client object and attaches any event listeners.
 *
 * @param {} environment   The environment object that came from the server's environment route.
 */
function init(environment) {
  const isProd = environment.ENVIRONMENT === 'production';

  // on local when running from the web container, local vue is in a static folder, but when builing
  // for deployment into the swarm, it's at the root
  const staticPath = environment.ENVIRONMENT === 'local' ? 'static/' : '';

  manager = new Oidc.UserManager({
    userStore: new Oidc.WebStorageStateStore(),
    authority: environment.OIDC_AUTHORITY,
    client_id: environment.OIDC_CLIENT,
    redirect_uri: `${environment.WEB_URL_HOST}${environment.WEB_URL_ROOT}/${staticPath}oidcCallback.html`,
    popup_redirect_uri: `${environment.WEB_URL_HOST}${environment.WEB_URL_ROOT}/${staticPath}oidcCallback.html?type=signinPopup`,
    silent_redirect_uri: `${environment.WEB_URL_HOST}${environment.WEB_URL_ROOT}/${staticPath}oidcCallback.html?type=silent`,
    response_type: 'id_token token',
    scope: 'openid offline_access',
    post_logout_redirect_uri: `${environment.WEB_URL_HOST}${environment.WEB_URL_ROOT}/${staticPath}oidcCallback.html?type=signoutPopup`,
    automaticSilentRenew: true,
    loadUserInfo: true,
    metadata: {
      issuer: environment.OIDC_AUTHORITY,
      authorization_endpoint: `${environment.OIDC_AUTHORITY}/protocol/openid-connect/auth`,
      jwks_uri: `${environment.OIDC_AUTHORITY}/protocol/openid-connect/certs`,
      userinfo_endpoint: `${environment.OIDC_AUTHORITY}/protocol/openid-connect/userinfo`,
      end_session_endpoint: `${environment.OIDC_AUTHORITY}/protocol/openid-connect/logout`,

      introspection_endpoint: `${environment.OIDC_AUTHORITY}/protocol/openid-connect/token/introspect`,
      token_endpoint: `${environment.OIDC_AUTHORITY}/protocol/openid-connect/token`
    }
  });

  // user loaded event (when signing in)
  manager.events.addUserLoaded((user) => {
    let userDetails = null;
    const currentlyLoggedIn = store.getters['session/sessionIsLoggedIn'];

    if (user && !user.expired && user.profile) {
      userDetails = _.clone(user.profile);
      userDetails.access_token = user.access_token;
      saveUserToStore(userDetails);

      // log the event if it's an actual first time login
      analytics.event(analytics.events.LOGIN, {
        method: currentlyLoggedIn ? 'renew' : 'new'
      });
    }
    else {
      store.dispatch('session/logout');
    }
    eventBus.bus.$emit(eventBus.CHANNEL_AUTHENTICATED, userDetails);
  });

  // user unloaded even (when signing out)
  manager.events.addUserUnloaded(() => {
    store.dispatch('session/logout');
    eventBus.bus.$emit(eventBus.CHANNEL_AUTHENTICATED, null);
  });

  // try to get a user already in session
  manager.getUser().then((user) => {
    if (user && !user.expired && user.profile) {
      const userDetails = _.clone(user.profile);
      userDetails.access_token = user.access_token;
      saveUserToStore(userDetails);
      eventBus.bus.$emit(eventBus.CHANNEL_AUTHENTICATED, userDetails);

      analytics.event(analytics.events.LOGIN, {
        method: 'refresh'
      });
    }
  }).catch((getUserError) => {
    console.error('Error getting existing user from session');
    console.error(getUserError);
  }).finally(() => {
    // let the App know that we're done trying to get the user
    eventBus.bus.$emit(eventBus.CHANNEL_USER_LOADED);
  });

  Oidc.Log.logger = console;
  Oidc.Log.level = isProd ? Oidc.Log.NONE : Oidc.Log.DEBUG;
}

/**
 * Saves the user details to the user store.
 */
function saveUserToStore(userDetails) {
  store.dispatch('session/setSession', userDetails);
  store.dispatch('session/setSessionIsValidated', true);
}

/**
 * Opens a pop up to show the Open ID Connect login.  It should go directly to CAS.  If we dont' do a pop up, the
 * whole page will need to redirect somewhere and the hashmarks seems to be a problem.
 */
function signInPopup() {
  manager.signinPopup().catch((err) => {
    eventBus.bus.$emit(eventBus.CHANNEL_USER_AUTH_FAILED, err);
    console.error(err);

    // try to determine the error to display
    let errorString = `${String(err)}.  Please contact us with the error message above for help signing in.`;
    if (errorString.match(/is in the past/g) || errorString.match(/is in the future/g)) {
      errorString = 'Your computer clock is off by more than 10 minutes.  Please correct it by either setting the date and time automatically or manually setting it.  If you need help doing this, please contact us';
    }
    eventBus.bus.$emit(eventBus.CHANNEL_BUS_MESSAGE, {
      message: eventBus.MESSAGES.MESSAGE_MODAL_SHOW,
      data: {
        title: 'There was a problem signing in',
        message: errorString
      }
    });
  });
}

/**
 * Opens a pop up to complete the signout flow.  If we don't do a pop up, it seems the whole page will
 * need to redirect somewhere and we don't want to lose our place or anything.
 */
function signoutPopup() {
  manager.signoutPopup().catch((err) => {
    console.error(err);
  });
}

function signInSilent() {
  manager.signinSilent().catch((err) => {
    console.error(err);
  });
}

/* Define storage keys in one place to prevent unintentional collisions */
export default {
  init,
  signInPopup,
  signoutPopup,
  signInSilent,
  getUser: function () {
    return manager.getUser();
  }
};